import Button from '@mui/material/Button';
import { Link, Tooltip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';
import { getPackagingStockLevel } from 'src/utils/packaging-stock-level';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { IStationeryItemListItem } from 'src/types/stationery-item';

import StationeryItemAdjustQuantityDialog from './stationery-item-adjust-quantity-dialog';

type Props = {
  row: IStationeryItemListItem;
  onEditRow: VoidFunction;
  onDeleteRow: VoidFunction;
  onAdjusted?: VoidFunction;
};

export default function StationeryItemTableRow({ row, onEditRow, onDeleteRow, onAdjusted }: Props) {
  const { _id, itemName, itemCode, quantity, unit, reorderThreshold, updatedAt } = row;

  const { user: authUser } = useAuthContext();

  const confirm = useBoolean();

  const adjustDialog = useBoolean();

  const popover = usePopover();

  const stockLevel = getPackagingStockLevel(quantity, reorderThreshold);

  return (
    <>
      <Link
        component={RouterLink}
        href={paths.dashboard.stationeryItem.view(_id)}
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'contents',
        }}
      >
        <TableRow hover>
          <TableCell>
            <ListItemText
              primary={itemName}
              secondary={itemCode}
              primaryTypographyProps={{ typography: 'body2' }}
              secondaryTypographyProps={{
                component: 'span',
                color: 'text.disabled',
              }}
            />
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {quantity} {unit?.abbreviation}
          </TableCell>

          <TableCell sx={{ textAlign: 'left' }}>
            <Label variant="soft" color={stockLevel.color}>
              {stockLevel.label}
            </Label>
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {fDateTime(updatedAt)}
          </TableCell>

          <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
            <Link component={RouterLink} href={paths.dashboard.stationeryItem.view(_id)} mr={2}>
              <Button variant="soft" color="primary">
                View More
              </Button>
            </Link>

            {hasPermission(
              authUser,
              'any',
              AuthPermissions.UPDATE_STATIONERY_ITEM,
              AuthPermissions.DELETE_STATIONERY_ITEM,
              AuthPermissions.ADJUST_STATIONERY_ITEM_QUANTITY,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Tooltip title="More options">
                <IconButton
                  color={popover.open ? 'inherit' : 'default'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    popover.onOpen(e);
                  }}
                >
                  <Iconify icon="eva:more-vertical-fill" />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      </Link>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 180 }}
      >
        {hasPermission(
          authUser,
          'any',
          AuthPermissions.UPDATE_STATIONERY_ITEM,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <MenuItem
            onClick={() => {
              onEditRow();
              popover.onClose();
            }}
          >
            <Iconify icon="solar:pen-bold" />
            Edit
          </MenuItem>
        )}

        {hasPermission(
          authUser,
          'any',
          AuthPermissions.ADJUST_STATIONERY_ITEM_QUANTITY,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <MenuItem
            onClick={() => {
              adjustDialog.onTrue();
              popover.onClose();
            }}
          >
            <Iconify icon="eva:swap-fill" />
            Adjust Quantity
          </MenuItem>
        )}

        {hasPermission(
          authUser,
          'any',
          AuthPermissions.DELETE_STATIONERY_ITEM,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <MenuItem
            onClick={() => {
              confirm.onTrue();
              popover.onClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <Iconify icon="solar:trash-bin-trash-bold" />
            Delete
          </MenuItem>
        )}
      </CustomPopover>

      {adjustDialog.value && (
        <StationeryItemAdjustQuantityDialog
          item={row}
          open={adjustDialog.value}
          onClose={adjustDialog.onFalse}
          onSuccess={onAdjusted}
        />
      )}

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete Stationery Item"
        content={`Are you sure you want to delete ${itemName}? This is blocked if any purchase orders, dispatches, or returns already reference it.`}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              confirm.onFalse();
              onDeleteRow();
            }}
          >
            Delete
          </Button>
        }
      />
    </>
  );
}
